import React from 'react';
import Joi from 'joi-browser';
import PropTypes from 'prop-types';
import {
  mapValues,
  values,
  includes,
} from 'lodash';
import { connect } from 'react-redux';
import {
  withStyles,
  CardContent,
  Button,
  InputLabel,
  OutlinedInput,
  FormControl,
  FormHelperText,
  FormLabel,
  Card,
  CardActions,
  CardHeader,
  Avatar,
  CircularProgress,
  IconButton,
  Grid,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import * as profileKeys from '@pingum/api-services/UserService/profileKeys';

import { updateUser } from '../../../../redux/user/actions';
import AvatarEditor from '../../../widgets/AvatarEditor';
import styles from './styles';

import './styles.scss';

let initialState = {};

export class UserProfileCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.avatarEditorRef = React.createRef();

    initialState = {
      updateUserRequest: {
        firstName: this.props.profile[profileKeys.firstName],
        lastName: this.props.profile[profileKeys.lastName],
        email: this.props.profile[profileKeys.email],
        phone: this.props.profile[profileKeys.phone],
        avatar: this.props.profile[profileKeys.avatar],
      },
      validation: {
        hasChanged: false,
        firstName: {
          isValid: true,
          message: '',
        },
        lastName: {
          isValid: true,
          message: '',
        },
        email: {
          isValid: true,
          message: '',
        },
        phone: {
          isValid: true,
          message: '',
        },
      },
    };

    this.state = initialState;
  }

  getAvatarSrc = () => {
    if (this.state.updateUserRequest.avatar && this.state.updateUserRequest.avatar.indexOf('http') >= 0) {
      // to avoid browser cache append random param
      return `${this.state.updateUserRequest.avatar}?date=${new Date().toISOString()}`;
    }
    return this.state.updateUserRequest.avatar;
  }

  updateUserData = () => {
    if (!(includes(values(mapValues(this.state.validation, (validation) => {
      if (validation instanceof Object) {
        return validation.isValid;
      }
      return validation;
    })), false))) {
      this.props.updateUser(this.props.profile.user_id, this.state.updateUserRequest);
      this.setState((prevState) => ({
        validation: {
          ...prevState.validation,
          hasChanged: false,
        },
      }));
    }
  }

  handleFirstNameChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
        && changedValue !== undefined
        && changedValue.length >= 1
        && changedValue.length <= 50;
    this.setState((prevState) => ({
      updateUserRequest: {
        ...prevState.updateUserRequest,
        firstName: changedValue,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        firstName: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid first name (between 1 and 50 characters in length).</span>
            : '',
        },
      },
    }));
  }

  handleLastNameChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
        && changedValue !== undefined
        && changedValue.length >= 1
        && changedValue.length <= 50;
    this.setState((prevState) => ({
      updateUserRequest: {
        ...prevState.updateUserRequest,
        lastName: changedValue,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        lastName: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid last name (between 1 and 50 characters in length).</span>
            : '',
        },
      },
    }));
  }

  handleEmailChange = (e) => {
    const changedValue = e.target.value;
    const isValid = Joi.validate(changedValue, Joi.string().email()).error === null;
    this.setState((prevState) => ({
      updateUserRequest: {
        ...prevState.updateUserRequest,
        email: changedValue,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        email: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid email address</span>
            : '',
        },
      },
    }));
  }

  handlePhoneChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = (
        changedValue === null
          || changedValue === undefined
          || changedValue.length === 0
      )
        ? true
        : Joi.validate(changedValue, Joi.string()).error === null
            && changedValue.length >= 10;
    this.setState((prevState) => ({
      updateUserRequest: {
        ...prevState.updateUserRequest,
        phone: changedValue,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        phone: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid phone number</span>
            : '',
        },
      },
    }));
  }

  handleAvatarChange = (avatar) => {
    this.setState((prevState) => ({
      updateUserRequest: {
        ...prevState.updateUserRequest,
        avatar,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
      },
    }));
    if (!avatar) {
      if (this.avatarEditorRef.current) {
        this.avatarEditorRef.current.onCancel();
      }
    }
  }

  resetState = () => {
    this.setState(initialState);
    if (this.avatarEditorRef.current) {
      this.avatarEditorRef.current.onCancel();
    }
  }

  showActionButtons = () => (this.props.isFetching || this.state.validation.hasChanged || this.props.updateUserError)
      && this.state.validation.email.isValid
      && this.state.validation.phone.isValid
      && this.state.validation.firstName.isValid
      && this.state.validation.lastName.isValid

  render() {
    return (
      <Card>
        <CardHeader
          title="Profile Information"
          subheader="Display and Contact Information"
        />
        <CardContent>
          <div className={this.props.classes.container}>
            <Grid item xs={12} sm={8}>
              <FormControl
                error={!this.state.validation.firstName.isValid}
                margin="normal"
                className={this.props.classes.firstName}
                variant="outlined"
              >
                <InputLabel htmlFor="user-first-name">First Name</InputLabel>
                <OutlinedInput
                  id="first-name-field"
                  autoComplete="given-name"
                  autoCapitalize="words"
                  value={this.state.updateUserRequest.firstName}
                  onChange={this.handleFirstNameChange}
                  disabled={this.props.isFetching}
                  labelWidth={80}
                />
                <FormHelperText id="user-first-name-helper-text">
                  {
                    !this.state.validation.firstName.isValid
                    && this.state.validation.firstName.message
                  }
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl
                error={!this.state.validation.lastName.isValid}
                margin="normal"
                className={this.props.classes.lastName}
                variant="outlined"
              >
                <InputLabel htmlFor="user-last-name">Last Name</InputLabel>
                <OutlinedInput
                  id="last-name-field"
                  autoComplete="family-name"
                  autoCapitalize="words"
                  value={this.state.updateUserRequest.lastName}
                  onChange={this.handleLastNameChange}
                  disabled={this.props.isFetching}
                  labelWidth={80}
                />
                <FormHelperText id="user-last-name-helper-text">
                  {
                    !this.state.validation.lastName.isValid
                    && this.state.validation.lastName.message
                  }
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl
                error={!this.state.validation.email.isValid}
                margin="normal"
                aria-describedby="user-email-helper-text"
                className={this.props.classes.email}
                variant="outlined"
              >
                <InputLabel htmlFor="tenant-name">Email</InputLabel>
                <OutlinedInput
                  id="email-field"
                  autoComplete="email"
                  autoCapitalize="words"
                  value={this.state.updateUserRequest.email}
                  onChange={this.handleEmailChange}
                  disabled
                  labelWidth={45}
                />
                <FormHelperText id="user-email-helper-text">
                  Email cannot be modified. Contact Us for help.
                  {/* {
                    !this.state.validation.email.isValid &&
                    this.state.validation.email.message
                  } */}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* Removing this field as we do not use it for anything today */}
            {/* <FormControl
              error={!this.state.validation.phone.isValid}
              margin="normal"
              aria-describedby="user-phone-helper-text"
              className={this.props.classes.textField}
            >
              <InputLabel htmlFor="tenant-name" shrink>Phone</InputLabel>
              <Input
                id="phone-field"
                autoComplete="tel-national"
                autoCapitalize="words"
                placeholder="(555) 555-555"
                value={this.state.updateUserRequest.phone}
                onChange={this.handlePhoneChange}
                inputComponent={PhoneInput}
              />
              <FormHelperText id="user-phone-helper-text">
                {
                  !this.state.validation.phone.isValid &&
                  this.state.validation.phone.message
                }
              </FormHelperText>
            </FormControl> */}
            <Grid item xs={12} sm={8}>
              <FormControl
                error={!this.state.validation.phone.isValid}
                margin="normal"
                aria-describedby="user-change-avatar"
                className={this.props.classes.avatarField}
              >
                <div className={this.props.classes.profileImageContainer}>
                  <div className={this.props.classes.flex}>
                    <FormLabel>
                      Profile Image
                    </FormLabel>
                  </div>
                  <div>
                    {
                      (this.getAvatarSrc() && this.getAvatarSrc().indexOf('gravatar.com') === -1)
                        && (
                          <IconButton
                            className={this.props.classes.removeAvatarButton}
                            aria-label="Remove Upload"
                            onClick={() => { this.handleAvatarChange(this.props.profile.picture); }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )
                    }
                  </div>
                  <div>
                    <Avatar
                      src={this.getAvatarSrc()}
                      className={this.props.classes.avatarIcon}
                    />
                  </div>
                </div>
                <AvatarEditor
                  ref={this.avatarEditorRef}
                  onChange={this.handleAvatarChange}
                />
              </FormControl>
            </Grid>
          </div>
        </CardContent>
        <CardActions>
          <Button
            disabled={!(this.state.validation.hasChanged || this.props.updateUserError)}
            className={this.props.classes.actionButton}
            onClick={this.resetState}
          >
            Cancel
            {
              this.props.isFetching
              && <CircularProgress size={20} style={{ marginLeft: 10 }} color="inherit" />
            }
          </Button>
          <Button
            disabled={!this.showActionButtons()}
            color="primary"
            onClick={this.updateUserData}
            variant="contained"
          >
            Save
            {
              this.props.isFetching
                && <CircularProgress size={20} style={{ marginLeft: 10 }} color="inherit" />
            }
          </Button>
        </CardActions>
      </Card>
    );
  }
}

UserProfileCardComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  updateUser: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  updateUserError: PropTypes.bool.isRequired,
};

export const mapStateToProps = (state) => ({
  profile: state.user.user,
  isFetching: state.user.isFetchingUser,
  updateUserError: state.user.updateUserError,
});

export const mapDispatchToProps
  = (dispatch) => ({
    updateUser: (auth0UserId, updateUserRequest) => dispatch(
      updateUser(auth0UserId, updateUserRequest),
    ),
  });

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(UserProfileCardComponent));
